import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { useApolloClient } from "@apollo/client"
import { Intercom } from "@intercom/messenger-js-sdk"

import styles from "./Fin.module.scss"

import { graphql } from "@/public/idp_gql/__generated__"
import finArrowIcon from "@/public/images/fin_arrow_bottom_right.svg"
import finChatIcon from "@/public/images/fin_chat.svg"
import finErrorIcon from "@/public/images/fin_error.svg"

const VerifyFinTokenQuery = graphql(/* GraphQL */ `
	query VerifyFinToken($param: VerifyFinTokenParam!) {
		verifyFinToken(param: $param) {
			result {
				intercomUserId
				intercomUserHash
				isFirstAccess
				finJwt
				companyName
			}
			error
		}
	}
`)

type FinState =
	| { type: "loading" }
	| { type: "authenticated"; companyName: string | null }
	| { type: "error"; message: string }

const HELP_CENTER_URL =
	"https://refundhub-help.notion.site/RefundHub-ad994c70cc064210b3b6b70a8485a01e"

export default function Fin() {
	const [searchParams] = useSearchParams()
	const [state, setState] = useState<FinState>({ type: "loading" })
	const apolloClient = useApolloClient()

	useEffect(() => {
		const token = searchParams.get("token")
		const message = searchParams.get("message")

		if (!token) {
			setState({ type: "error", message: "認証トークンが必要です" })
			return
		}

		apolloClient
			.query({
				query: VerifyFinTokenQuery,
				variables: { param: { token } },
				fetchPolicy: "no-cache",
			})
			.then((res) => {
				const { result, error } = res.data.verifyFinToken

				if (error === "EXPIRED") {
					setState({
						type: "error",
						message:
							"このリンクの有効期限が切れています。新しいリンクをお問い合わせください。",
					})
					return
				}

				if (error === "INVALID" || !result) {
					setState({ type: "error", message: "認証に失敗しました。" })
					return
				}

				Intercom({
					app_id: "rq41wsdb",
					user_id: result.intercomUserId,
					user_hash: result.intercomUserHash,
					auth_tokens: {
						line_group_id_jwt: "Bearer " + result.finJwt,
					},
				})

				if (result.isFirstAccess && message) {
					setTimeout(() => {
						if (window.Intercom) {
							window.Intercom("showNewMessage", message)
						}
					}, 1000)
				}

				setState({ type: "authenticated", companyName: result.companyName })
			})
			.catch(() => {
				setState({ type: "error", message: "認証に失敗しました。" })
			})
	}, [searchParams, apolloClient])

	if (state.type === "error") {
		return (
			<div className={styles.stateContainer}>
				<div className={styles.stateCard}>
					<div className={styles.errorIcon}>
						<img src={finErrorIcon} alt="error" />
					</div>
					<p className={styles.errorText}>{state.message}</p>
				</div>
			</div>
		)
	}

	if (state.type === "loading") {
		return (
			<div className={styles.stateContainer}>
				<div className={styles.stateCard}>
					<div className={styles.spinner} />
					<p className={styles.loadingText}>認証中...</p>
				</div>
			</div>
		)
	}

	return (
		<div className={styles.container}>
			<div className={styles.hero}>
				<div className={styles.heroIcon}>
					<img src={finChatIcon} alt="chat" />
				</div>
				<h1 className={styles.heroTitle}>リファンドハブ AIチャットボット</h1>
				<p className={styles.heroDescription}>
					画面右下のチャットアイコンから
					<br />
					お気軽にお問い合わせください
				</p>
				<button
					type="button"
					className={styles.pointerArea}
					onClick={() => {
						if (window.Intercom) {
							window.Intercom("show")
						}
					}}
				>
					<span className={styles.pointerText}>右下のアイコンをタップ</span>
					<span className={styles.pointerArrow}>
						<img src={finArrowIcon} alt="arrow" />
					</span>
				</button>
			</div>

			<div className={styles.guideFooter}>
				<a
					href={HELP_CENTER_URL}
					target="_blank"
					rel="noopener noreferrer"
					className={styles.guideLink}
				>
					ヘルプページはこちら
				</a>
				{state.companyName !== null && (
					<p className={styles.companyNotice}>※ {state.companyName} 様専用</p>
				)}
			</div>
		</div>
	)
}
